import { useCallback, useMemo, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type Row,
  type ColumnFiltersState,
  type SortingState,
} from '@tanstack/react-table';

import useEntityDialogState from 'shared/hooks/useEntityDialogState';
import useSelector from 'shared/hooks/useSelector';
import Dialog from 'shared/components/Dialog';
import Grid from 'shared/components/Grid';
import CommonCase from 'containers/pages/common-case';

import useIsConstructor from '../../lib/useIsConstructor';
import useGetCooperativeCommonCasesListRequestParameters from '../../lib/useGetCooperativeCommonCasesListRequestParameters';
import {
  selectFilteredCasesBySearchTerm,
  selectUniqueCaseLevel1List,
  selectUniqueStatusesList,
  selectUniqueYearsList,
} from '../../models/selectors';
import type { CooperativeCommonCase } from '../../models';
import { createColumns } from './cols';

type CooperativeCommonCasesGridProps = {
  isFetching: boolean;
};

const CooperativeCommonCasesGrid = ({
  isFetching,
}: CooperativeCommonCasesGridProps) => {
  const { t } = useTranslation();

  const isConstructor = useIsConstructor();
  const entityDialogState = useEntityDialogState();
  const requestParams = useGetCooperativeCommonCasesListRequestParameters();

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([
    { desc: true, id: 'CreatedOn' },
  ]);

  const { caseLevel1Options, list, statusOptions, yearOptions } = useSelector(
    (state) => ({
      caseLevel1Options: selectUniqueCaseLevel1List(state, requestParams),
      list: selectFilteredCasesBySearchTerm(state, requestParams),
      statusOptions: selectUniqueStatusesList(state, requestParams),
      yearOptions: selectUniqueYearsList(state, requestParams),
    })
  );

  const columns = useMemo(
    () => createColumns({ t, caseLevel1Options, statusOptions, yearOptions }),
    [t, caseLevel1Options, statusOptions, yearOptions]
  );

  const table = useReactTable({
    columns,
    data: list,
    state: {
      columnFilters,
      sorting,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
  });

  const handleClickRow = useCallback(
    (
      _event: MouseEvent<HTMLTableRowElement>,
      row: Row<CooperativeCommonCase>
    ) => {
      entityDialogState.onOpen(row.original.Id);
    },
    []
  );

  return (
    <>
      <Grid isLoading={isFetching} table={table} onClickRow={handleClickRow} />
      <Dialog
        open={entityDialogState.open}
        fullWidth
        maxWidth="lg"
        onClose={entityDialogState.onClose}
        TransitionProps={{
          onExited: entityDialogState.onClearId,
        }}
      >
        <CommonCase
          id={entityDialogState.id!}
          constructorCase={isConstructor}
        />
      </Dialog>
    </>
  );
};

export default CooperativeCommonCasesGrid;
